import { useEffect, useRef, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { BuildingsIcon, SlidersHorizontalIcon, UserCircleIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { LatticeField } from "@libs/zapi-ui/ds/components/lattice-field";
import { Reveal } from "@libs/zapi-ui/ds/components/reveal";
import { SidebarProvider } from "@libs/zapi-ui/ds/components/sidebar";
import { distance } from "@libs/zapi-ui/lib/motion";
import {
  AppShellCenter,
  AppShellContent,
  AppShellMain,
  AppShellPanels
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/AppShell";
import {
  ProjectNav,
  type ProjectNavGroup
} from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ProjectLayout/ui/blocks/ProjectNav";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasAdminPrivileges } from "@/zapi-frontend/shared/utils/permissions";
import { useSettingsNavStore } from "@/zapi-frontend/stores/settingsNav";
import { ProfileSection } from "./ProfileSection";
import { PreferencesSection } from "./_PreferencesSection";
import { GeneralSection } from "./_GeneralSection";
import { TeamSection } from "./TeamSection";

export type TSettingsTab = "Team" | "Profile" | "General" | "Preferences";

const ACCOUNT_TABS: TSettingsTab[] = ["Profile", "Preferences"];
const ORGANIZATION_TABS: TSettingsTab[] = ["General", "Team"];

const screens: Record<TSettingsTab, ComponentType> = {
  Profile: ProfileSection,
  Preferences: PreferencesSection,
  General: GeneralSection,
  Team: TeamSection
};

// Held open: the provider's ⌘B toggle would otherwise fire while typing in a field.
const keepOpen = () => undefined;

export function SettingsPage() {
  const { t } = useTranslation(["settings", "projectHome"]);
  const isAdmin = hasAdminPrivileges(useMeStore((state) => state.role));
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navOpen = useSettingsNavStore((state) => state.open);
  const setNavOpen = useSettingsNavStore((state) => state.setOpen);
  useEffect(() => () => setNavOpen(false), [setNavOpen]);

  const tabs = isAdmin ? [...ACCOUNT_TABS, ...ORGANIZATION_TABS] : ACCOUNT_TABS;
  const requested = searchParams.get("tab") as TSettingsTab | null;
  const tab = requested && tabs.includes(requested) ? requested : "Profile";
  const Screen = screens[tab];
  // The page arrives as a whole; after that, a chosen tab's content arrives on its own.
  const switched = useRef(false);

  function handleTabChange(next: TSettingsTab) {
    switched.current = true;
    setNavOpen(false);
    setSearchParams({ tab: next }, { state: location.state });
  }

  const groups: ProjectNavGroup[] = [
    {
      id: "account",
      label: t("groups.account"),
      items: [
        { id: "Profile", label: t("tabs.profile"), icon: UserCircleIcon, href: "#Profile" },
        { id: "Preferences", label: t("tabs.preferences"), icon: SlidersHorizontalIcon, href: "#Preferences" }
      ]
    },
    ...(isAdmin
      ? [
          {
            id: "organization",
            label: t("groups.organization"),
            items: [
              { id: "General", label: t("tabs.general"), icon: BuildingsIcon, href: "#General" },
              { id: "Team", label: t("tabs.team"), icon: UsersThreeIcon, href: "#Team" }
            ]
          }
        ]
      : [])
  ];

  return (
    <div className="flex flex-1">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <LatticeField />
      </div>
      <SidebarProvider
        open
        onOpenChange={keepOpen}
        openMobile={navOpen}
        onOpenMobileChange={setNavOpen}
        className="min-h-0 w-auto shrink-0"
      >
        <ProjectNav
          groups={groups}
          activeId={tab}
          label={t("title")}
          lockedLabel={t("projectHome:sidebar.locked")}
          lockedIconLabel={t("projectHome:sidebar.lockedIcon")}
          renderLink={(item) => (
            <button
              type="button"
              data-testid={`settings-sidebar-${item.id.toLowerCase()}-button`}
              onClick={() => handleTabChange(item.id as TSettingsTab)}
            />
          )}
        />
      </SidebarProvider>
      <AppShellPanels>
        <AppShellCenter>
          <AppShellMain className="mx-0 max-w-[960px] min-w-0 px-0 pt-4 pb-10 sm:px-0">
            <AppShellContent size="wide" className="flex flex-col gap-6 data-[size=wide]:max-w-none">
              {switched.current ? (
                <Reveal key={tab} blurred={false} offset={distance.md} className="flex min-w-0 flex-col gap-6">
                  <Screen />
                </Reveal>
              ) : (
                <div className="flex min-w-0 flex-col gap-6">
                  <Screen />
                </div>
              )}
            </AppShellContent>
          </AppShellMain>
        </AppShellCenter>
      </AppShellPanels>
    </div>
  );
}
